import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { DEFAULT_BRAND, getBranding } from './branding';

const BrandingContext = createContext(DEFAULT_BRAND);

export const BrandingProvider = ({ children }) => {
  const branding = useMemo(() => getBranding(), []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const title = branding.siteTitle || branding.name;
    const description = branding.metaDescription || branding.copy?.description || title;
    const socialImage = branding.socialImage || branding.logos?.primary;
    const themeColor = branding.themeColor || branding.colors?.accent;
    const baseUrl = process.env.PUBLIC_URL || '';
    const resolveAssetUrl = (rawUrl) => {
      if (!rawUrl) return rawUrl;
      const scopedUrl = rawUrl.startsWith('/') ? `${baseUrl}${rawUrl}` : rawUrl;
      if (typeof window === 'undefined' || !window.location?.origin) return scopedUrl;

      try {
        return new URL(scopedUrl, window.location.origin).href;
      } catch (_error) {
        return scopedUrl;
      }
    };

    const updateMeta = (attribute, key, content) => {
      if (!content) return;
      let meta = document.querySelector(`meta[${attribute}="${key}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, key);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateLink = (rel, href) => {
      if (!href) return;
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    if (title) {
      document.title = title;
    }

    updateMeta('name', 'description', description);
    updateMeta('property', 'og:title', title);
    updateMeta('property', 'og:description', description);
    updateMeta('property', 'og:image', resolveAssetUrl(socialImage));
    updateMeta('name', 'twitter:title', title);
    updateMeta('name', 'twitter:description', description);
    updateMeta('name', 'twitter:image', resolveAssetUrl(socialImage));
    updateMeta('name', 'theme-color', themeColor);

    const faviconHref = resolveAssetUrl(branding.favicon);
    updateLink('icon', faviconHref);
    updateLink('shortcut icon', faviconHref);
    updateLink('apple-touch-icon', resolveAssetUrl(branding.logos?.mark || branding.favicon));
  }, [branding]);
  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
